import { z } from 'zod';
import {
    type CardPointeBrowserTokenRecordingGatewayResponse,
    type CardPointeHostedIframeTokenRecordingGatewayResponse,
    type CardPointeRecordTokenGatewayResponse,
    type CardPointeRecordTokenNormalizedResult,
    type CardPointeRecordTokenRequest,
    type CardPointeRecordTokenResponse,
} from '../../../types/ipg/cardpointe/cardpointe-token-recording.types';
import { CardPointeGatewayId } from '../../../types/ipg/cardpointe/cardpointe-system-constants';
import { CardPointeTokenizationResultType } from '../../../types/ipg/cardpointe/cardpointe-tokenization.types';
import {
    CardPointeCardSecureTokenizationSuccessResponseSchema,
    CardPointeCardSecureBrowserTokenizationResultV2Schema,
    CardPointeCardSecureHostedIframeTokenizationResultV2Schema,
    CardPointeHostedIframeSuccessResponseSchema,
    CardPointeStoredCardMetadataTokenizationResultSchema,
    CardPointeTokenizedCardMetadataSchema,
    CardPointeTokenizationTokenSchema,
} from './cardpointe-tokenization.schemas';
import { CardTypeSchema, CustomerDetailsSchema } from '../../samba-payment.schemas';
import { StoredMaskedPanSchema } from '../../masked_pan.schemas';

export const CardPointeRecordTokenUuidSchema: z.ZodString = z.string().uuid();

export const CardPointeBrowserTokenRecordingGatewayResponseSchema: z.ZodObject<{
    result_type: z.ZodLiteral<
        typeof CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION
    >;
    cardsecure_response: typeof CardPointeCardSecureTokenizationSuccessResponseSchema;
    card_metadata: typeof CardPointeTokenizedCardMetadataSchema;
}> = z
    .object({
        result_type: z.literal(CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION),
        cardsecure_response: CardPointeCardSecureTokenizationSuccessResponseSchema,
        card_metadata: CardPointeTokenizedCardMetadataSchema,
    })
    .strict() satisfies z.ZodType<CardPointeBrowserTokenRecordingGatewayResponse>;

export const CardPointeHostedIframeTokenRecordingGatewayResponseSchema = z
    .object({
        result_type: z.literal(
            CardPointeTokenizationResultType.CARDSECURE_HOSTED_IFRAME_TOKENIZATION
        ),
        cardsecure_response: CardPointeHostedIframeSuccessResponseSchema,
        card_metadata: CardPointeTokenizedCardMetadataSchema,
    })
    .strict() satisfies z.ZodType<CardPointeHostedIframeTokenRecordingGatewayResponse>;

export const CardPointeRecordTokenGatewayResponseSchema: z.ZodType<CardPointeRecordTokenGatewayResponse> =
    z.union([
        CardPointeBrowserTokenRecordingGatewayResponseSchema,
        CardPointeCardSecureBrowserTokenizationResultV2Schema.safeExtend({
            card_metadata: CardPointeTokenizedCardMetadataSchema,
        }),
        CardPointeHostedIframeTokenRecordingGatewayResponseSchema,
        CardPointeCardSecureHostedIframeTokenizationResultV2Schema.safeExtend({
            card_metadata: CardPointeTokenizedCardMetadataSchema,
        }),
        CardPointeStoredCardMetadataTokenizationResultSchema,
    ]);

export const CardPointeRecordTokenRequestSchema: z.ZodObject<{
    terminal_device_uuid: typeof CardPointeRecordTokenUuidSchema;
    card_type: typeof CardTypeSchema;
    card_brand: z.ZodString;
    masked_pan: typeof StoredMaskedPanSchema;
    cardholder_name: z.ZodString;
    gateway_response: typeof CardPointeRecordTokenGatewayResponseSchema;
    customer_details: typeof CustomerDetailsSchema;
}> = z
    .object({
        terminal_device_uuid: CardPointeRecordTokenUuidSchema,
        card_type: CardTypeSchema,
        card_brand: z.string().min(1),
        masked_pan: StoredMaskedPanSchema,
        cardholder_name: z.string().min(1),
        gateway_response: CardPointeRecordTokenGatewayResponseSchema,
        customer_details: CustomerDetailsSchema,
    })
    .strict() satisfies z.ZodType<CardPointeRecordTokenRequest>;

export const CardPointeRecordTokenResponseSchema: z.ZodObject<{
    success: z.ZodLiteral<true>;
    payment_method_token_uuid: typeof CardPointeRecordTokenUuidSchema;
}> = z
    .object({
        success: z.literal(true),
        payment_method_token_uuid: CardPointeRecordTokenUuidSchema,
    })
    .strict() satisfies z.ZodType<CardPointeRecordTokenResponse>;

export const CardPointeRecordTokenNormalizedResultSchema: z.ZodObject<{
    gateway_id: z.ZodLiteral<typeof CardPointeGatewayId>;
    gateway_token: typeof CardPointeTokenizationTokenSchema;
    card_type: typeof CardTypeSchema;
    card_brand: z.ZodString;
    masked_pan: typeof StoredMaskedPanSchema;
    cardholder_name: z.ZodString;
}> = z
    .object({
        gateway_id: z.literal(CardPointeGatewayId),
        gateway_token: CardPointeTokenizationTokenSchema,
        card_type: CardTypeSchema,
        card_brand: z.string().min(1),
        masked_pan: StoredMaskedPanSchema,
        cardholder_name: z.string().min(1),
    })
    .strict() satisfies z.ZodType<CardPointeRecordTokenNormalizedResult>;
